import { Polar } from "@polar-sh/sdk";

const POLAR_ACCESS_TOKEN = import.meta.env.VITE_POLAR_ACCESS_TOKEN;
const POLAR_ORG_ID = import.meta.env.VITE_POLAR_ORG_ID;

if (!POLAR_ACCESS_TOKEN) {
  console.warn('VITE_POLAR_ACCESS_TOKEN is not set');
}

if (!POLAR_ORG_ID) {
  console.warn('VITE_POLAR_ORG_ID is not set');
}

const polar = new Polar({
  accessToken: POLAR_ACCESS_TOKEN ?? "",
});

export interface CustomerMeter {
  id: string;
  created_at: string;
  modified_at: string;
  customer_id: string;
  meter_id: string;
  consumed_units: number;
  credited_units: number;
  balance: number;
  customer: {
    id: string;
    email: string;
    name?: string;
    organization_id: string;
  };
}

export interface CustomerMeterResponse {
  items: CustomerMeter[];
  pagination: {
    total_count: number;
    max_page: number;
  };
}

/**
 * Get customer credit meters from Polar
 * @param customerId - Optional customer ID to filter by
 * @returns Customer meter data including credit balance
 */
export async function getCustomerMeters(customerId?: string): Promise<CustomerMeter[]> {
  if (!POLAR_ORG_ID) {
    throw new Error('Polar organization ID is not configured');
  }

  try {
    const result = await polar.customerMeters.list({
      organizationId: POLAR_ORG_ID,
      ...(customerId && { customerId }),
    });

    const meters: CustomerMeter[] = [];
    for await (const page of result) {
      // The SDK returns pages directly, not wrapped in items
      if (Array.isArray(page)) {
        meters.push(...page);
      } else if (page && typeof page === 'object' && 'items' in page) {
        meters.push(...((page as any).items || []));
      }
    }

    return meters;
  } catch (error) {
    console.error('Error fetching customer meters from Polar:', error);
    throw error;
  }
}

/**
 * Get credit balance for a specific customer
 * @param customerId - Polar customer ID
 * @returns Credit balance or null if not found
 */
export async function getCustomerCreditBalance(customerId: string): Promise<number | null> {
  try {
    const meters = await getCustomerMeters(customerId);

    // Sum up all credit balances across meters
    const totalBalance = meters.reduce((sum, meter) => sum + meter.balance, 0);

    return totalBalance;
  } catch (error) {
    console.error('Error fetching customer credit balance:', error);
    return null;
  }
}

/**
 * Get customer meter details including consumed and credited units
 * @param customerId - Polar customer ID
 * @returns Detailed meter information
 */
export async function getCustomerMeterDetails(customerId: string): Promise<{
  balance: number;
  consumed: number;
  credited: number;
} | null> {
  try {
    const meters = await getCustomerMeters(customerId);

    const totals = meters.reduce(
      (acc, meter) => ({
        balance: acc.balance + meter.balance,
        consumed: acc.consumed + meter.consumed_units,
        credited: acc.credited + meter.credited_units,
      }),
      { balance: 0, consumed: 0, credited: 0 }
    );

    return totals;
  } catch (error) {
    console.error('Error fetching customer meter details:', error);
    return null;
  }
}

/**
 * Check if customer has sufficient credits
 * @param customerId - Polar customer ID
 * @param requiredCredits - Number of credits required
 * @returns True if customer has enough credits
 */
export async function hasEnoughCredits(
  customerId: string,
  requiredCredits: number
): Promise<boolean> {
  const balance = await getCustomerCreditBalance(customerId);
  return balance !== null && balance >= requiredCredits;
}

/**
 * Create a customer session for managing subscription
 * @param externalCustomerId - External customer ID (e.g., Supabase user ID)
 * @returns Customer portal URL for managing subscription
 */
export async function createCustomerSession(externalCustomerId: string): Promise<string | null> {
  try {
    const result = await polar.customerSessions.create({
      externalCustomerId,
    });

    return result.customerPortalUrl || null;
  } catch (error) {
    console.error('Error creating customer session:', error);
    return null;
  }
}

/**
 * Get customer portal URL for subscription management
 * @param userId - Supabase user ID
 * @returns Portal URL or null if error
 */
export async function getCustomerPortalUrl(userId: string): Promise<string | null> {
  return await createCustomerSession(userId);
}

/**
 * Create a checkout session for subscription purchase
 * @param customerEmail - Customer's email address
 * @param userId - User's ID for external customer tracking
 * @param productIds - Array of Polar product IDs to include in checkout
 * @param discountId - Optional discount/campaign ID to apply
 * @returns Checkout URL or null if error
 */
export async function createCheckoutSession(
  customerEmail: string,
  userId: string,
  productIds: string[],
  discountId?: string
): Promise<string | null> {
  try {
    const checkoutData: {
      customerEmail: string;
      externalCustomerId: string;
      products: string[];
      discountId?: string;
    } = {
      customerEmail,
      externalCustomerId: userId,
      products: productIds,
    };

    // Add discount_id if provided
    if (discountId) {
      checkoutData.discountId = discountId;
    }

    const result = await polar.checkouts.create(checkoutData);

    return result.url || null;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return null;
  }
}

/**
 * Create a checkout session for subscription purchase
 * @param customerEmail - Customer's email address
 * @param userId - User's ID for external customer tracking
 * @param productIds - Array of Polar product IDs to include in checkout
 * @param discountId - Optional discount/campaign ID to apply
 * @returns Checkout URL or null if error
 */
export async function createAnonCheckoutSession(
  productIds: string[],
  discountId?: string
): Promise<string | null> {
  try {
    const checkoutData: {
      products: string[];
      discount_id?: string;
    } = {
      products: productIds,
    };

    // Add discount_id if provided
    if (discountId) {
      checkoutData.discount_id = discountId;
    }

    const result = await polar.checkouts.create(checkoutData);

    return result.url || null;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return null;
  }
}

export interface PolarCustomerState {
  id: string;
  email: string;
  name?: string;
  activeSubscriptions: Array<{
    id: string;
    status: string;
    productId: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    meters?: Array<{
      meterId: string;
      consumedUnits: number;
      creditedUnits: number;
      amount: number;
    }>;
  }>;
  activeMeters: Array<{
    id: string;
    meterId: string;
    consumedUnits: number;
    creditedUnits: number;
    balance: number;
  }>;
}

/**
 * Get customer state from Polar using external ID (Supabase user ID)
 * @param externalId - External customer ID (Supabase user ID)
 * @returns Customer state with subscriptions and meters
 */
export async function getCustomerState(externalId: string): Promise<PolarCustomerState | null> {
  try {
    const result = await polar.customers.getStateExternal({
      externalId,
    });

    return result as unknown as PolarCustomerState;
  } catch (error: any) {
    // 404 is expected for users without a Polar customer record - silently return null
    // Check various error formats from Polar SDK
    const isNotFound =
      error?.status === 404 ||
      error?.statusCode === 404 ||
      error?.body$?.includes('ResourceNotFound') ||
      error?.error === 'ResourceNotFound' ||
      error?.detail === 'Not found' ||
      (typeof error === 'object' && error?.error === 'ResourceNotFound');

    if (isNotFound) {
      return null;
    }

    // Only log unexpected errors
    console.error('Error fetching customer state:', error);
    return null;
  }
}

/**
 * Get customer subscription status and tier from Polar
 * @param externalId - External customer ID (Supabase user ID)
 * @returns Subscription info or null
 */
export async function getCustomerSubscriptionInfo(externalId: string): Promise<{
  tier: string;
  status: string;
  productId: string | null;
  currentPeriodEnd: string | null;
  polarCustomerId: string | null;
  polarSubscriptionId: string | null;
} | null> {
  try {
    const state = await getCustomerState(externalId);

    if (!state || !state.activeSubscriptions || state.activeSubscriptions.length === 0) {
      return null;
    }

    // Get the first active subscription
    const subscription = state.activeSubscriptions[0];

    // Map product IDs to tiers
    const productTierMap: Record<string, string> = {
      "4e8e2ce6-1f6f-41a2-a233-64447c5441d4": "weekly",
      "4ecdfad9-ed9f-464d-b66f-93ef90e3ac83": "solo",
      "814de567-163b-48a4-afae-4cb8a91d21ba": "pro",
      "8a798a28-22d0-4443-9916-7d13a9579edc": "pro_byok",
    };

    const tier = productTierMap[subscription.productId] || "solo";

    return {
      tier,
      polarCustomerId: state.id,
      polarSubscriptionId: subscription.id,
      status: subscription.status,
      productId: subscription.productId,
      currentPeriodEnd: subscription.currentPeriodEnd,
    };
  } catch (error) {
    console.error('Error fetching customer subscription info:', error);
    return null;
  }
}

/**
 * Sync Polar customer state to Supabase profile
 * @param userId - Supabase user ID (used as external ID in Polar)
 * @returns Success boolean
 */
export async function syncPolarCustomerToProfile(
  userId: string
): Promise<boolean> {
  try {
    const subscriptionInfo = await getCustomerSubscriptionInfo(userId);

    if (!subscriptionInfo) {
      return false;
    }

    const { supabase } = await import('@/integrations/supabase/client');

    const { error } = await supabase
      .from('profiles')
      .update({
        polar_customer_id: subscriptionInfo.polarCustomerId,
        polar_subscription_id: subscriptionInfo.polarSubscriptionId,
        subscription_tier: subscriptionInfo.tier as any,
        subscription_status: subscriptionInfo.status as any,
        subscription_end_date: subscriptionInfo.currentPeriodEnd,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId);

    if (error) {
      console.error('Error syncing Polar customer to profile:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in syncPolarCustomerToProfile:', error);
    return false;
  }
}

export interface PolarDiscount {
  id: string;
  name: string;
  code?: string;
  type: 'fixed' | 'percentage';
  amount?: number; // For fixed discounts (in cents)
  basis_points?: number; // For percentage discounts (10000 = 100%, 1500 = 15%)
  duration: 'once' | 'repeating' | 'forever';
  duration_in_months?: number; // For repeating discounts
  starts_at?: string | null; // ISO date string
  ends_at?: string | null; // ISO date string
  max_redemptions?: number | null;
  redemptions_count?: number;
  created_at: string;
  modified_at?: string | null;
  organization_id: string;
  metadata?: Record<string, any>;
  products?: Array<{
    id: string;
    name: string;
    description?: string;
    [key: string]: any;
  }>;
}

function isPolarResourceNotFoundError(error: any): boolean {
  return (
    error?.status === 404 ||
    error?.statusCode === 404 ||
    error?.body$?.includes('ResourceNotFound') ||
    error?.error === 'ResourceNotFound' ||
    error?.detail === 'Not found' ||
    (typeof error === 'object' && error?.error === 'ResourceNotFound')
  );
}

/**
 * Check if a discount is currently active from already-fetched discount data.
 */
export function isDiscountCurrentlyActive(discount: PolarDiscount | null): boolean {
  if (!discount) {
    return false;
  }

  const now = new Date();

  // Check start date
  if (discount.starts_at) {
    const startDate = new Date(discount.starts_at);
    if (now < startDate) {
      return false;
    }
  }

  // Check end date
  if (discount.ends_at) {
    const endDate = new Date(discount.ends_at);
    if (now > endDate) {
      return false;
    }
  }

  // Check redemption limits
  if (
    discount.max_redemptions !== null &&
    discount.max_redemptions !== undefined &&
    discount.redemptions_count !== undefined &&
    discount.redemptions_count >= discount.max_redemptions
  ) {
    return false;
  }

  return true;
}

/**
 * Get discount information from Polar
 * @param discountId - Polar discount ID
 * @returns Discount information or null if not found/error
 */
export async function getDiscount(discountId: string): Promise<PolarDiscount | null> {
  if (!POLAR_ORG_ID) {
    console.warn('Polar organization ID is not configured');
    return null;
  }

  try {
    const result = await polar.discounts.get({
      id: discountId,
    });

    return result as unknown as PolarDiscount;
  } catch (error: any) {
    // Missing discounts are expected when old campaign IDs are still configured.
    if (isPolarResourceNotFoundError(error)) {
      return null;
    }

    console.error('Error fetching discount from Polar:', error);
    return null;
  }
}

/**
 * Check if a discount is currently active based on start/end dates and redemption limits
 * @param discountId - Polar discount ID
 * @returns True if discount is active, false otherwise
 */
export async function isDiscountActive(discountId: string): Promise<boolean> {
  try {
    const discount = await getDiscount(discountId);
    return isDiscountCurrentlyActive(discount);
  } catch (error) {
    console.error('Error checking discount status:', error);
    return false;
  }
}
